import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { crmApi } from '../services/crmApi';
import { Bell, AlertTriangle, Info, CheckCircle, XCircle, ChevronRight, X, Settings, Wifi, WifiOff } from 'lucide-react';

interface SystemNotification {
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  description: string;
  action: string;
}

interface TeamNotification {
  id: number;
  title: string;
  message: string;
  type: string;
  action_url: string | null;
  is_read: boolean;
  created_at: string;
}

const DISMISSED_KEY = 'dismissed_notifications';

function getDismissed(): string[] {
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); } catch { return []; }
}

function setDismissed(titles: string[]) {
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(titles));
}

function formatTime(d: string): string {
  try {
    const date = new Date(d);
    const diff = Date.now() - date.getTime();
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return `${Math.floor(diff / 86400000)}d`;
  } catch { return ''; }
}

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [systemNotifs, setSystemNotifs] = useState<SystemNotification[]>([]);
  const [teamNotifs, setTeamNotifs] = useState<TeamNotification[]>([]);
  const [teamUnread, setTeamUnread] = useState(0);
  const [dismissed, setDismissedState] = useState<string[]>(getDismissed);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'system' | 'personal'>('personal');
  const [sseConnected, setSseConnected] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const eventSourceRef = useRef<EventSource | null>(null);

  const loadAll = useCallback(async () => {
    try {
      const [sysData, teamData] = await Promise.all([
        crmApi.getNotifications(),
        crmApi.getTeamNotifications(true),
      ]);
      if (mountedRef.current) {
        setSystemNotifs(sysData.notifications || []);
        setTeamNotifs(teamData.notifications || []);
        setTeamUnread(teamData.unread || 0);
      }
    } catch { /* */ }
  }, []);

  // SSE connection — replaces polling
  useEffect(() => {
    mountedRef.current = true;
    loadAll(); // Initial fetch

    const token = localStorage.getItem('token');
    if (!token) return;

    const apiBase = (import.meta as any).env?.VITE_API_URL || '';
    const sseUrl = `${apiBase}/api/dashboard/sse?token=${encodeURIComponent(token)}`;

    let reconnectTimer: ReturnType<typeof setTimeout>;
    let retryDelay = 2000;

    function connect() {
      if (!mountedRef.current) return;
      const es = new EventSource(sseUrl);
      eventSourceRef.current = es;

      es.addEventListener('connected', () => {
        setSseConnected(true);
        retryDelay = 2000; // reset backoff on success
      });

      // Real-time notification events — refresh data instantly
      es.addEventListener('notification', () => { loadAll(); });
      es.addEventListener('team_notification', () => { loadAll(); });

      es.onerror = () => {
        setSseConnected(false);
        es.close();
        eventSourceRef.current = null;
        // Exponential backoff: 2s → 4s → 8s → ... → max 60s
        reconnectTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 60000);
      };
    }

    connect();

    // Fallback: slow poll every 5 min in case SSE drops silently
    const fallbackInterval = setInterval(loadAll, 300_000);

    return () => {
      mountedRef.current = false;
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      clearTimeout(reconnectTimer);
      clearInterval(fallbackInterval);
    };
  }, [loadAll]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleDismiss = (title: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = [...dismissed, title];
    setDismissedState(updated);
    setDismissed(updated);
  };

  const handleMarkRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    await crmApi.markNotificationsRead([id]);
    setTeamNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setTeamUnread(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const unreadIds = teamNotifs.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length > 0) {
      await crmApi.markNotificationsRead(unreadIds);
      setTeamNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
      setTeamUnread(0);
    }
    // Dismiss all system notifications too
    const allTitles = systemNotifs.map(n => n.title);
    setDismissedState(allTitles);
    setDismissed(allTitles);
  };

  const visibleSystem = systemNotifs.filter(n => !dismissed.includes(n.title));
  const totalCount = visibleSystem.length + teamUnread;

  const iconMap = {
    info: <Info size={14} className="text-blue-500" />,
    warning: <AlertTriangle size={14} className="text-yellow-500" />,
    error: <XCircle size={14} className="text-red-500" />,
    success: <CheckCircle size={14} className="text-green-500" />,
  };

  const bgMap: Record<string, string> = {
    info: 'hover:bg-blue-50',
    warning: 'hover:bg-yellow-50',
    error: 'hover:bg-red-50',
    success: 'hover:bg-green-50',
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2.5 rounded-lg transition-all"
        aria-label="Powiadomienia"
      >
        <Bell size={20} />
        {totalCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
        <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${sseConnected ? 'bg-emerald-400' : 'bg-gray-400'}`} title={sseConnected ? 'Live' : 'Offline'} />
      </button>

      {open && (
        <div className="absolute left-0 top-12 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border z-50 overflow-hidden">
          {/* Header with tabs */}
          <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
            <div className="flex gap-1">
              <button onClick={() => setActiveTab('personal')}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  activeTab === 'personal' ? 'bg-rose-100 text-rose-700' : 'text-gray-500 hover:bg-gray-200'
                }`}>
                Moje {teamUnread > 0 && <span className="ml-1 bg-rose-500 text-white text-[9px] px-1 rounded-full">{teamUnread}</span>}
              </button>
              <button onClick={() => setActiveTab('system')}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  activeTab === 'system' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-200'
                }`}>
                System {visibleSystem.length > 0 && <span className="ml-1 bg-blue-500 text-white text-[9px] px-1 rounded-full">{visibleSystem.length}</span>}
              </button>
            </div>
            <div className="flex items-center gap-1">
              {totalCount > 0 && (
                <button onClick={handleMarkAllRead}
                  className="text-[10px] text-gray-400 hover:text-blue-600 px-1" title="Oznacz wszystkie jako przeczytane">
                  Wyczyść
                </button>
              )}
              <button onClick={() => { navigate('/notifications'); setOpen(false); }}
                className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-600" title="Centrum powiadomień">
                <Settings size={14} />
              </button>
            </div>
          </div>

          {/* Personal notifications */}
          {activeTab === 'personal' && (
            teamNotifs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Brak nowych powiadomien
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {teamNotifs.slice(0, 20).map(n => (
                  <div
                    key={n.id}
                    className={`px-4 py-3 border-b last:border-0 flex items-start gap-3 hover:bg-gray-50 transition-colors ${!n.is_read ? 'bg-rose-50/30' : ''}`}
                  >
                    <button
                      type="button"
                      onClick={() => { if (n.action_url) navigate(n.action_url); setOpen(false); }}
                      className="flex-1 flex items-start gap-3 min-w-0 text-left"
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {iconMap[n.type as keyof typeof iconMap] || iconMap.info}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{n.title}</p>
                          {!n.is_read && <div className="w-1.5 h-1.5 bg-rose-500 rounded-full shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-500 line-clamp-2">{n.message}</p>
                        <span className="text-[10px] text-gray-400">{formatTime(n.created_at)}</span>
                      </div>
                    </button>
                    {!n.is_read && (
                      <button type="button" onClick={(e) => handleMarkRead(n.id, e)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0" title="Oznacz jako przeczytane">
                        <CheckCircle size={12} className="text-gray-400" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}

          {/* System notifications */}
          {activeTab === 'system' && (
            visibleSystem.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Brak alertow systemowych
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {visibleSystem.map((n, i) => (
                  <div
                    key={i}
                    className={`w-full text-left px-4 py-3 border-b last:border-0 flex items-start gap-3 ${bgMap[n.type]} transition-colors`}
                  >
                    <button
                      type="button"
                      onClick={() => { navigate(n.action); setOpen(false); }}
                      className="flex-1 flex items-start gap-3 min-w-0"
                    >
                      <div className="mt-0.5 flex-shrink-0">{iconMap[n.type]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{n.title}</p>
                        <p className="text-xs text-gray-500">{n.description}</p>
                      </div>
                      <ChevronRight size={14} className="text-gray-300 mt-1 flex-shrink-0" />
                    </button>
                    <button type="button" onClick={(e) => handleDismiss(n.title, e)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0" title="Odrzuć">
                      <X size={12} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-2 border-t">
            <button onClick={() => { navigate('/notifications'); setOpen(false); }}
              className="w-full text-center text-xs text-rose-600 font-medium hover:underline">
              Otwórz centrum powiadomien
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
